import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Collapse,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Add as AddIcon,
  Update as UpdateIcon,
  SkipNext as SkipIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import {
  LeaseCreationPreview,
  ConflictResolution,
  downloadDebugLogs,
  CalculatedConcession,
} from './leaseImportHelpers';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index} role="tabpanel">
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

interface LeaseRowProps {
  preview: LeaseCreationPreview;
  onResolutionChange?: (
    unitId: string,
    resolution: ConflictResolution
  ) => void;
}

// Helper to format concession info
// NOTE: This function is kept for potential future use but concession calculation
// from rent difference was removed from the import phase as it was inaccurate.
function formatConcessionSummary(concession: CalculatedConcession | undefined): string | null {
  if (!concession) return null;
  const { monthsOfConcession, totalConcessionAmount, monthlyRent, effectiveRent } = concession;
  return `${monthsOfConcession.toFixed(1)} months ($${totalConcessionAmount.toLocaleString()} total)`;
}

function LeaseRow({ preview, onResolutionChange }: LeaseRowProps) {
  const [expanded, setExpanded] = useState(false);
  const { details, importData, existingLease } = preview;

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return 'N/A';
    if (typeof date === 'string') {
      return dayjs(date, 'YYYY-MM-DD').format('MM/DD/YYYY');
    }
    return dayjs(date).format('MM/DD/YYYY');
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined) return 'N/A';
    return `$${amount.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const hasDetails =
    details.startDateChanged ||
    details.endDateChanged ||
    details.rentChanged ||
    details.depositChanged;

  const getTypeChip = () => {
    // If there's a conflict, show conflict chip
    if (preview.hasConflict) {
      const tooltipText =
        preview.conflictType === 'vacant_conflict'
          ? 'Unit is vacant but has an active lease'
          : preview.conflictType === 'dates_overlap'
            ? 'Dates overlap with existing lease'
            : 'Dates differ from existing lease';
      return (
        <Tooltip title={tooltipText}>
          <Chip
            size="small"
            icon={<WarningIcon />}
            label="Conflict"
            color="error"
            variant="outlined"
          />
        </Tooltip>
      );
    }

    // Check if it's a past/expired lease
    if (preview.conflictType === 'past_lease' || preview.isPastLease) {
      return (
        <Chip
          size="small"
          label="Past Lease"
          color="warning"
          variant="outlined"
        />
      );
    }

    // Check if it's a renewal
    if (preview.conflictType === 'renewal') {
      return (
        <Chip
          size="small"
          label="Renewal"
          color="info"
          variant="outlined"
        />
      );
    }

    // If action is 'create', it's a new lease (including new separate lease periods)
    if (preview.action === 'create') {
      return (
        <Chip
          size="small"
          icon={<AddIcon />}
          label="New"
          color="success"
          variant="outlined"
        />
      );
    }

    // Otherwise it's an update (rent/deposit changed on same lease period)
    return (
      <Chip
        size="small"
        icon={<UpdateIcon />}
        label="Update"
        color="warning"
        variant="outlined"
      />
    );
  };

  return (
    <>
      <TableRow
        sx={{
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
          cursor: hasDetails ? 'pointer' : 'default',
          backgroundColor: preview.hasConflict
            ? 'rgba(244, 67, 54, 0.08)'
            : undefined,
        }}
        onClick={() => hasDetails && setExpanded(!expanded)}
      >
        <TableCell onClick={(e) => e.stopPropagation()} sx={{ minWidth: 140 }}>
          {(preview.hasConflict || preview.conflictType === 'past_lease') && onResolutionChange && (
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <Select
                value={preview.selectedResolution || 'skip'}
                onChange={(e) =>
                  onResolutionChange(
                    preview.unitId,
                    e.target.value as ConflictResolution
                  )
                }
                size="small"
              >
                <MenuItem value="skip">Skip</MenuItem>
                <MenuItem value="skip_and_check">Skip & Check</MenuItem>
                {preview.conflictType === 'renewal' && (
                  <MenuItem value="renewal">Renew</MenuItem>
                )}
                {preview.conflictType === 'past_lease' && (
                  <MenuItem value="past_lease">Add Past Lease</MenuItem>
                )}
                {preview.conflictType === 'vacant_conflict' && (
                  <MenuItem value="end_existing">End existing</MenuItem>
                )}
                {preview.conflictType === 'vacant_conflict' && (
                  <MenuItem value="cancel_existing">Cancel existing</MenuItem>
                )}
                {preview.conflictType !== 'vacant_conflict' && (
                  <MenuItem value="overwrite_existing">Overwrite</MenuItem>
                )}
                {preview.conflictType !== 'vacant_conflict' && (
                  <MenuItem value="cancel_existing_create_new">Cancel & new</MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </TableCell>
        <TableCell>
          {hasDetails && (
            <IconButton size="small">
              {expanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="medium">
            {preview.unitId}
          </Typography>
        </TableCell>
        <TableCell>{getTypeChip()}</TableCell>
        <TableCell align="right">
          {formatCurrency(importData.monthlyRent)}
        </TableCell>
        <TableCell align="right">
          -
        </TableCell>
        <TableCell align="right">
          {formatDate(importData.leaseStartDate)}
        </TableCell>
        <TableCell align="right">
          {formatDate(importData.leaseEndDate)}
        </TableCell>
      </TableRow>
      {hasDetails && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2, px: 4, backgroundColor: 'rgba(0,0,0,0.2)' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {preview.reason}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Changes from existing lease:
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell align="right">Current</TableCell>
                      <TableCell align="right">New</TableCell>
                      <TableCell align="right">Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.rentChanged && (
                      <TableRow>
                        <TableCell>Monthly Rent</TableCell>
                        <TableCell align="right">
                          {formatCurrency(details.rentChanged.from)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(details.rentChanged.to)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color:
                              details.rentChanged.percentChange >= 0
                                ? 'success.main'
                                : 'error.main',
                          }}
                        >
                          {details.rentChanged.percentChange >= 0 ? '+' : ''}
                          {details.rentChanged.percentChange.toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    )}
                    {details.startDateChanged && (
                      <TableRow>
                        <TableCell>Start Date</TableCell>
                        <TableCell align="right">
                          {formatDate(details.startDateChanged.from)}
                        </TableCell>
                        <TableCell align="right">
                          {formatDate(details.startDateChanged.to)}
                        </TableCell>
                        <TableCell align="right">-</TableCell>
                      </TableRow>
                    )}
                    {details.endDateChanged && (
                      <TableRow>
                        <TableCell>End Date</TableCell>
                        <TableCell align="right">
                          {formatDate(details.endDateChanged.from)}
                        </TableCell>
                        <TableCell align="right">
                          {formatDate(details.endDateChanged.to)}
                        </TableCell>
                        <TableCell align="right">-</TableCell>
                      </TableRow>
                    )}
                    {details.depositChanged && (
                      <TableRow>
                        <TableCell>Security Deposit</TableCell>
                        <TableCell align="right">
                          {formatCurrency(details.depositChanged.from)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(details.depositChanged.to)}
                        </TableCell>
                        <TableCell align="right">-</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function SkippedLeaseRow({ preview }: LeaseRowProps) {
  const [expanded, setExpanded] = useState(false);
  const { details } = preview;

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return 'N/A';
    if (typeof date === 'string') {
      return dayjs(date, 'YYYY-MM-DD').format('MM/DD/YYYY');
    }
    return dayjs(date).format('MM/DD/YYYY');
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined) return 'N/A';
    return `$${amount.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const hasDetails =
    details.startDateChanged ||
    details.endDateChanged ||
    details.rentChanged ||
    details.depositChanged;

  const getSkipChip = () => {
    if (preview.conflictType === 'early_start_date') {
      return (
        <Chip
          size="small"
          label="Early Start"
          color="warning"
          variant="outlined"
        />
      );
    }
    if (preview.conflictType === 'past_lease' || preview.isPastLease) {
      return (
        <Chip
          size="small"
          label="Past Lease"
          color="warning"
          variant="outlined"
        />
      );
    }
    return (
      <Chip
        size="small"
        icon={<SkipIcon />}
        label="Skip"
        sx={{
          borderColor: 'text.secondary',
          color: 'text.secondary',
        }}
        variant="outlined"
      />
    );
  };

  return (
    <>
      <TableRow
        sx={{
          opacity: 0.7,
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
          cursor: hasDetails ? 'pointer' : 'default',
        }}
        onClick={() => hasDetails && setExpanded(!expanded)}
      >
        <TableCell>
          {hasDetails && (
            <IconButton size="small">
              {expanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="medium">
            {preview.unitId}
          </Typography>
        </TableCell>
        <TableCell>{getSkipChip()}</TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {preview.reason}
          </Typography>
        </TableCell>
        <TableCell align="right">
          {formatCurrency(preview.importData.monthlyRent)}
        </TableCell>
        <TableCell align="right">
          {formatDate(preview.importData.leaseStartDate)}
        </TableCell>
        <TableCell align="right">
          {formatDate(preview.importData.leaseEndDate)}
        </TableCell>
      </TableRow>
      {hasDetails && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2, px: 4, backgroundColor: 'rgba(0,0,0,0.2)' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Difference from existing lease:
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell align="right">Existing</TableCell>
                      <TableCell align="right">Import</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.startDateChanged && (
                      <TableRow>
                        <TableCell>Start Date</TableCell>
                        <TableCell align="right">
                          {formatDate(details.startDateChanged.from)}
                        </TableCell>
                        <TableCell align="right">
                          {formatDate(details.startDateChanged.to)}
                        </TableCell>
                      </TableRow>
                    )}
                    {details.endDateChanged && (
                      <TableRow>
                        <TableCell>End Date</TableCell>
                        <TableCell align="right">
                          {formatDate(details.endDateChanged.from)}
                        </TableCell>
                        <TableCell align="right">
                          {formatDate(details.endDateChanged.to)}
                        </TableCell>
                      </TableRow>
                    )}
                    {details.rentChanged && (
                      <TableRow>
                        <TableCell>Monthly Rent</TableCell>
                        <TableCell align="right">
                          {formatCurrency(details.rentChanged.from)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(details.rentChanged.to)}
                        </TableCell>
                      </TableRow>
                    )}
                    {details.depositChanged && (
                      <TableRow>
                        <TableCell>Security Deposit</TableCell>
                        <TableCell align="right">
                          {formatCurrency(details.depositChanged.from)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(details.depositChanged.to)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

interface LeaseCreationPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (previews: LeaseCreationPreview[]) => void;
  toCreate: LeaseCreationPreview[];
  toSkip: LeaseCreationPreview[];
  summary: {
    totalImported: number;
    newLeases: number;
    updatedLeases: number;
    skipped: number;
  };
}

export function LeaseCreationPreviewDialog({
  open,
  onClose,
  onConfirm,
  toCreate: initialToCreate,
  toSkip,
  summary,
}: LeaseCreationPreviewDialogProps) {
  const [tabValue, setTabValue] = useState(0);
  const [toCreate, setToCreate] =
    useState<LeaseCreationPreview[]>(initialToCreate);

  // Update local state when props change
  React.useEffect(() => {
    setToCreate(initialToCreate);
  }, [initialToCreate]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleResolutionChange = (
    unitId: string,
    resolution: ConflictResolution
  ) => {
    setToCreate((prev) =>
      prev.map((p) =>
        p.unitId === unitId ? { ...p, selectedResolution: resolution } : p
      )
    );
  };

  const handleConfirm = () => {
    onConfirm(toCreate);
  };

  // Count how many will actually be processed (not skipped by user)
  const activeCount = toCreate.filter(
    (p) => p.selectedResolution !== 'skip'
  ).length;
  const conflictCount = toCreate.filter((p) => p.hasConflict).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h5">Lease Import Preview</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Review the changes that will be made before confirming the import.
          {conflictCount > 0 && (
            <Typography
              component="span"
              color="error.main"
              sx={{ display: 'block', mt: 0.5 }}
            >
              {conflictCount} lease(s) have date conflicts - please choose how
              to resolve them.
            </Typography>
          )}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Summary Section */}
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            mb: 3,
            p: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 1,
          }}
        >
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="h4" color="primary">
              {summary.totalImported}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total in CSV
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="h4" color="success.main">
              {summary.newLeases}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              New Leases
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="h4" color="warning.main">
              {summary.updatedLeases}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Updates
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="h4" color="text.tertiary">
              {summary.skipped}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Skipped (unchanged)
            </Typography>
          </Box>
          {conflictCount > 0 && (
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="h4" color="error.main">
                {conflictCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Conflicts
              </Typography>
            </Box>
          )}
        </Box>

        {/* Tabs for Create vs Skip */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab
              label={`To Process (${toCreate.length})`}
              sx={{
                color: toCreate.length > 0 ? 'success.main' : 'text.disabled',
              }}
            />
            <Tab
              label={`Skipped (${toSkip.length})`}
              sx={{ color: 'text.secondary' }}
            />
          </Tabs>
        </Box>

        {/* Create Tab */}
        <TabPanel value={tabValue} index={0}>
          {toCreate.length === 0 ? (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 4 }}
            >
              No new leases to create. All imported data matches existing
              leases.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 140 }}>Action</TableCell>
                    <TableCell sx={{ minWidth: 50 }} />
                    <TableCell sx={{ minWidth: 100 }}>Unit</TableCell>
                    <TableCell sx={{ minWidth: 80 }}>Type</TableCell>
                    <TableCell align="right" sx={{ minWidth: 80 }}>Rent</TableCell>
                    <TableCell align="right" sx={{ minWidth: 120 }}>Concession</TableCell>
                    <TableCell align="right" sx={{ minWidth: 90 }}>Start</TableCell>
                    <TableCell align="right" sx={{ minWidth: 90 }}>End</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {toCreate.map((preview) => (
                    <LeaseRow
                      key={preview.unitId}
                      preview={preview}
                      onResolutionChange={handleResolutionChange}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Skip Tab */}
        <TabPanel value={tabValue} index={1}>
          {toSkip.length === 0 ? (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 4 }}
            >
              No leases will be skipped.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 50 }} />
                    <TableCell>Unit</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell align="right">Rent</TableCell>
                    <TableCell align="right">Start</TableCell>
                    <TableCell align="right">End</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {toSkip.map((preview) => (
                    <SkippedLeaseRow key={preview.unitId} preview={preview} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={downloadDebugLogs}
          color="info"
          size="small"
          variant="outlined"
        >
          Download Debug Logs
        </Button>
        <Box>
          <Button onClick={onClose} color="inherit" sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={activeCount === 0}
          >
            Confirm Import ({activeCount} lease
            {activeCount !== 1 ? 's' : ''})
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
